import React from 'react';
import type { ListItemButtonBaseProps, SxProps } from '@mui/material';
import { ListItem as ListItemBase, ListItemButton } from '@mui/material';
import { match } from 'ts-pattern';
import Typography, { TypographyVariant } from '../Typography';
import type { ITheme } from '../../styles/theme';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import {
  ELEMENT_ID_DATA_LOG_ATTRIBUTE,
  ELEMENT_ID_DATA_TEST_ATTRIBUTE,
} from '../../../constants/data-element-tracking-ids';

type ListItemSize = 'medium' | 'small';

interface IListItemProps extends IPropsWithTestId {
  theme: ITheme;
  size?: ListItemSize;
  typographyVariant?: TypographyVariant;
  disableRipple?: boolean;
  additionalStyles?: SxProps;
}

export const getListItemStyles = (size: ListItemSize, theme: ITheme) => {
  const { colors } = theme;

  const defaultColors = {
    backgroundColor: colors.grayscale.white,
    color: colors.secondary,
    '&.Mui-focused, &.Mui-focusVisible': {
      backgroundColor: colors.grayscale.white,
    },
    '&:hover': {
      backgroundColor: colors.grayscale.lightGrey,
    },
  };

  const selectedColors = {
    backgroundColor: colors.neutralBackgroundColor,
    '&.Mui-focused, &.Mui-focusVisible': {
      backgroundColor: colors.neutralBackgroundColor,
    },
    '&:hover': {
      backgroundColor: colors.grayscale.lightGrey,
    },
  };

  const disabledColors = {
    backgroundColor: colors.grayscale.backgroundGrey,
    color: colors.grayscale.inactiveGrey1,
    opacity: 1,
  };

  const colorStyles = {
    ...defaultColors,
    '&[aria-selected="true"]': defaultColors,
    '&.Mui-selected': selectedColors,
    '&.Mui-disabled': disabledColors,
  };

  return {
    padding: `11px ${size === 'medium' ? 18 : 10}px`,
    ...colorStyles,
    '&.MuiAutocomplete-option': colorStyles,
  };
};

export const getListItemTypographyVariant = (
  size: ListItemSize,
  selected?: boolean,
) =>
  match({ size, selected })
    .with(
      { size: 'medium', selected: true },
      () => TypographyVariant.MEDIUM_BODY_BOLD,
    )
    .with(
      { size: 'medium', selected: false },
      () => TypographyVariant.MEDIUM_BODY_REGULAR,
    )
    .with({ size: 'small' }, () => TypographyVariant.SMALL_BODY_REGULAR)
    .otherwise(() => TypographyVariant.MEDIUM_BODY_REGULAR);

/**
 * https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?type=design&node-id=62-349&mode=design&t=pCLNs5WoHQ4CsVUh-4
 */
const ListItem: React.FC<ListItemButtonBaseProps & IListItemProps> = ({
  theme,
  size = 'medium',
  typographyVariant,
  additionalStyles = {},
  disableRipple,
  dataElementId,
  children,
  ...props
}) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <ListItemBase sx={{ padding: 0 }}>
    <ListItemButton
      disableRipple={disableRipple}
      sx={{ ...getListItemStyles(size, theme), ...additionalStyles }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...{
        [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: dataElementId,
        [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: dataElementId,
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <Typography
        variant={
          typographyVariant ??
          getListItemTypographyVariant(size, props.selected)
        }
        theme={theme}
        color="inherit"
      >
        {children}
      </Typography>
    </ListItemButton>
  </ListItemBase>
);

export default ListItem;
